import type { CalibrationRow, DebriefView, Pacing, SessionConfig } from '@nightshift/engine/client';

export interface Cohort { id: string; name: string; createdAt: number }
type Dashboard = DebriefView['tables'][number]['dashboard'];
type OptionalMetric = 'idleCapacity' | 'plannedEscalations' | 'crisisEscalations' | 'lateEscalations' | 'resources' | 'overflowDelays';
type RetiredMetric = 'pressureTurns' | 'turnedAway';
export type CohortResult = Pick<DebriefView['tables'][number], 'calibration' | 'objectives' | 'prompts'> & {
  dashboard: Omit<Dashboard, OptionalMetric | RetiredMetric> & Partial<Pick<Dashboard, OptionalMetric>> & Partial<Record<RetiredMetric, number>>;
  sessionId: string; table: number; preset: string; pacing: string; seats: number; engineVersion: string; endedAt: number;
  pacingConfig: Pacing; layers: SessionConfig['layers']; completion: 'full' | 'early' | 'unknown';
};
export interface CohortGroup { key: string; rows: CohortResult[]; mean: CohortResult['dashboard']; calibration: CalibrationRow[] }

/** Compare identical settings and pool card counts before calculating rates. */
export function groupResults(rows: readonly CohortResult[]): CohortGroup[] {
  const groups = new Map<string, CohortResult[]>();
  for (const row of [...rows].sort((a, b) => a.endedAt - b.endedAt || a.sessionId.localeCompare(b.sessionId) || a.table - b.table)) {
    const p = row.pacingConfig;
    const key = JSON.stringify([row.preset, row.seats, row.engineVersion, row.pacing, p.key, p.scriptTurns,
      p.arrivalsMs, p.huddleMs, p.actMs, p.tickMs, p.logMs, p.handoverMs, row.layers.staff, row.layers.vents, row.layers.blood,
      row.completion, row.completion === 'full' ? null : row.sessionId]);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }
  return [...groups].map(([key, members]) => {
    const metrics = (Object.keys(members[0].dashboard) as (keyof CohortResult['dashboard'])[]).filter(metric => metric !== 'pressureTurns' && metric !== 'turnedAway');
    const mean = Object.fromEntries(metrics.map((metric) => {
      const values = members.map((row) => row.dashboard[metric]);
      return [metric, values.every((value): value is number => typeof value === 'number' && Number.isFinite(value))
        ? values.reduce((sum, value) => sum + value, 0) / members.length : undefined];
    })) as CohortResult['dashboard'];
    const pooled = new Map<number, CalibrationRow>();
    for (const row of members.flatMap((member) => member.calibration)) {
      const total = pooled.get(row.box) ?? { box: row.box, drawn: 0, drawnRight: 0, deck: 0, deckRight: 0 };
      total.drawn += row.drawn;
      total.drawnRight += row.drawnRight;
      total.deck += row.deck;
      total.deckRight += row.deckRight;
      pooled.set(row.box, total);
    }
    return { key, rows: members, mean, calibration: [...pooled.values()].sort((a, b) => b.box - a.box) };
  });
}
